<template>
  <div class="pa-4 pa-sm-6">
    <v-card flat class="sms-card">
      <!-- Breadcrumb + Back Button -->
      <v-row class="align-center bread-row ma-0" no-gutters>
        <v-col cols="9" sm="10">
          <Breadcrump :crumbs="crumbs" />
        </v-col>
        <v-col cols="3" sm="2" class="text-left">
          <v-btn @click="$router.go(-1)" fab x-small dark color="primary">
            <v-icon dark>mdi-arrow-left</v-icon>
          </v-btn>
        </v-col>
      </v-row>

      <!-- Title + Buttons -->
      <v-card-title class="pb-8 pt-4 title-font d-flex align-center flex-wrap">
        <span class="IranSans font-weight-medium">پیامک‌های دستگاه‌ها</span>
        <v-spacer></v-spacer>
      </v-card-title>

      <!-- Filters -->
      <v-card-text class="pt-0 pb-8">
        <v-row dense>
          <v-col cols="12" sm="6" md="4">
            <v-autocomplete
              v-model="filter.complexId"
              :items="complexes"
              item-value="id"
              item-text="text"
              label="مجموعه"
              :search-input.sync="searchComplex"
              outlined
              dense
              hide-details
              hide-selected
              no-data-text="موردی یافت نشد"
              @change="getSubComplexes"
              clearable
              @click:clear="clearComplex"
            >
              <template v-slot:prepend-inner>
                <v-icon size="20" color="grey">mdi-magnify</v-icon>
              </template></v-autocomplete
            >
          </v-col>
          <v-col cols="12" sm="6" md="4">
            <v-autocomplete
              v-model="selectedSubComplexId"
              :items="subComplexes"
              item-value="id"
              item-text="text"
              label="زیر مجموعه"
              :search-input.sync="searchSubComplex"
              outlined
              dense
              hide-details
              hide-selected
              no-data-text="موردی یافت نشد"
              @change="getDevices(selectedSubComplexId)"
              clearable
              @click:clear="getSubComplexes"
            >
              <template v-slot:prepend-inner>
                <v-icon size="20" color="grey">mdi-magnify</v-icon>
              </template></v-autocomplete
            >
          </v-col>
          <v-col cols="12" sm="6" md="4">
            <v-autocomplete
              v-model="filter.deviceId"
              :items="devices"
              item-value="id"
              item-text="text"
              label="دستگاه"
              :search-input.sync="searchDevice"
              outlined
              dense
              hide-details
              hide-selected
              no-data-text="موردی یافت نشد"
              clearable
            >
              <template v-slot:prepend-inner>
                <v-icon size="20" color="grey">mdi-magnify</v-icon>
              </template></v-autocomplete
            >
          </v-col>
        </v-row>
        <v-row dense>
          <!-- Text fields - responsive -->
          <v-col cols="12" sm="6" md="4">
            <v-text-field
              v-model="filter.phoneNumber"
              label="شماره تلفن"
              outlined
              dense
              clearable
              hide-details
            />
          </v-col>
          <v-col cols="12" sm="6" md="4">
            <v-text-field
              v-model="filter.lockerNo"
              label="شماره لاکر"
              type="number"
              outlined
              dense
              clearable
              hide-details
            />
          </v-col>
          <v-col cols="12" sm="6" md="4">
            <v-select
              v-model="filter.isSent"
              :items="sentItems"
              label="وضعیت"
              outlined
              dense
              clearable
              hide-details
            />
          </v-col>
          <v-col cols="12" sm="6" md="4">
            <v-select
              v-model="filter.smsType"
              :items="typeItems"
              label="نوع پیام"
              outlined
              dense
              clearable
              hide-details
            />
          </v-col>

          <!-- Date pickers - always 2 columns even on mobile -->
          <v-col cols="12" sm="6" md="4">
            <date-picker
              v-model="from"
              placeholder="از تاریخ"
              simple
              editable
            />
          </v-col>
          <v-col cols="12" sm="6" md="4">
            <date-picker v-model="to" placeholder="تا تاریخ" simple editable />
          </v-col>
        </v-row>
        <div class="d-flex flex-wrap gap-2 pt-6 mt-sm-0 justify-end">
          <v-btn
            text
            outlined
            color="secondary"
            v-can="'DeviceSms_ResendAll'"
            class="ml-1"
            :loading="resendAllLoading"
            @click="resendAllMessages"
          >
            <v-icon left>mdi-send</v-icon>
            ارسال مجدد همه
          </v-btn>
          <v-btn
            text
            outlined
            v-can="'DeviceSms_ResendSelected'"
            color="secondary"
            class="ml-1"
            :loading="resendLoading"
            @click="resendSelected"
          >
            <v-icon left>mdi-send-clock</v-icon>
            ارسال مجدد موارد انتخاب شده
          </v-btn>
          <v-btn text outlined color="primary" @click="applyFilter">
            <v-icon left>mdi-magnify</v-icon>
            جستجو
          </v-btn>
        </div>
      </v-card-text>

      <!-- Table -->
      <v-data-table
        :headers="headers"
        :items="items"
        :options.sync="options"
        :server-items-length="total"
        :loading="loading"
        :page.sync="filter.page"
        :items-per-page.sync="filter.itemsPerPage"
        @update:page="onPageChange"
        @update:items-per-page="onItemsPerPageChange"
        :footer-props="{
          showFirstLastPage: true,
          firstIcon: 'mdi-arrow-collapse-left',
          lastIcon: 'mdi-arrow-collapse-right',
          prevIcon: 'mdi-arrow-left',
          nextIcon: 'mdi-arrow-right',
          itemsPerPageOptions: [5, 10, 25, 50]
        }"
        item-key="id"
        v-model="selected"
        show-select
        no-data-text="پیامی وجود ندارد"
      >
        <!-- Missing slots added for completeness -->
        <template v-slot:item.phoneNumber="{ item }">
          <div class="font-weight-medium">{{ item.phoneNumber || "—" }}</div>
        </template>

        <template v-slot:item.lockerNumber="{ item }">
          <v-chip small label color="grey lighten-3">
            {{ item.lockerNumber || "—" }}
          </v-chip>
        </template>

        <template v-slot:item.messageText="{ item }">
          <div
            style="max-width: 280px; white-space: pre-wrap; font-size: 0.85rem;"
          >
            {{ item.messageText || "—" }}
          </div>
        </template>

        <template v-slot:item.isSent="{ item }">
          <v-chip
            :color="item.isSent ? 'green lighten-1' : 'red lighten-1'"
            small
            outlined
          >
            {{ item.isSent ? "ارسال شد" : "ناموفق" }}
          </v-chip>
        </template>

        <template v-slot:item.errorText="{ item }">
          <span class="grey--text text--darken-1 text-caption">
            {{ item.errorText || "—" }}
          </span>
        </template>

        <template v-slot:item.smsType="{ item }">
          <v-chip small outlined color="blue-grey lighten-1">
            {{ getType(item.smsType) }}
          </v-chip>
        </template>

        <template v-slot:item.startTime="{ item }">
          <span class="text-caption grey--text text--darken-2">
            {{ formatDate(item.startTime) }}
          </span>
        </template>

        <template v-slot:item.createdAt="{ item }">
          <span class="text-caption grey--text text--darken-2">
            {{ formatDate(item.createdAt) }}
          </span>
        </template>
      </v-data-table>
      <div v-if="selected.length > 0" class="mb-4 d-flex align-center">
        <span class="mr-3 grey--text">
          {{ selected.length }} پیام انتخاب شده
        </span>
        <v-btn
          text
          small
          outlined
          color="secondary"
          @click="selected = []"
          class="mr-2"
        >
          لغو انتخاب
        </v-btn>
      </div>
    </v-card>

    <!-- Resend Dialog -->
    <v-dialog v-model="showResendDialog" max-width="360">
      <v-card>
        <v-card-title class="text-subtitle-1 IranSans">تأیید</v-card-title>
        <v-card-text class="text-body-2 IranSans">
          {{ `ارسال مجدد همه پیام‌های ناموفق؟ (${total} پیام)` }}
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text small @click="showResendDialog = false">خیر</v-btn>
          <v-btn
            color="error"
            text
            small
            :loading="resendAllLoading"
            @click="confirmResend"
          >
            بله
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import request from "@/utils/request";
import moment from "moment-jalaali";
import Breadcrump from "@/components/common/Breadcrump.vue";
import _ from "lodash";

const today = moment().format("jYYYY/jMM/jDD");
const aMonthAgo = moment()
  .subtract(1, "month")
  .format("jYYYY/jMM/jDD");

export default {
  name: "DeviceSmsList",
  components: { Breadcrump },
  data() {
    return {
      loading: false,
      selected: [],
      resendLoading: false,
      resendAllLoading: false,
      showResendDialog: false,
      searchComplex: null,
      searchSubComplex: null,
      searchDevice: null,
      crumbs: [
        { text: "خانه", disabled: false, href: "/" },
        { text: "گزارش پیام‌های دستگاه‌ها", disabled: true }
      ],
      from: aMonthAgo,
      to: today,
      items: [],
      total: 0,
      options: {
        page: 1,
        itemsPerPage: 10
      },
      selectedSubComplexId: null,
      subComplexes: [],
      complexes: [],
      devices: [],
      filter: {
        phoneNumber: null,
        lockerNo: null,
        isSent: null,
        smsType: null,
        fromDate: null,
        toDate: null,
        deviceId: null,
        complexId: null,
        deviceIds: []
      },
      sentItems: [
        { text: "همه", value: null },
        { text: "ارسال شده", value: true },
        { text: "ناموفق", value: false }
      ],
      typeItems: [
        { text: "همه", value: null },
        { text: "نامشخص", value: 0 },
        { text: "شارژ", value: 1 },
        { text: "پشتیبانی", value: 2 },
        { text: "بازی", value: 3 }
      ],
      headers: [
        { text: "مجموعه", value: "complexName" },
        { text: "دستگاه", value: "deviceName" },
        { text: "شماره تلفن", value: "phoneNumber" },
        { text: "لاکر", value: "lockerNumber" },
        { text: "متن پیام", value: "messageText" },
        { text: "وضعیت", value: "isSent" },
        { text: "متن خطا", value: "errorText" },
        { text: "نوع پیام", value: "smsType" },
        { text: "زمان شارژ", value: "startTime" },
        { text: "زمان ثبت", value: "createdAt" }
      ],
      smsTypeMap: {
        0: "نامشخص",
        1: "شارژ",
        2: "پشتیبانی",
        3: "بازی"
      }
    };
  },
  mounted() {
    this.fetchData();
    this.getDevices(0);
    this.getComplexes();
  },
  created() {
    this.debouncedLoadMessages = _.debounce(this.fetchData, 300);
    this.debouncedLoadMessages = _.debounce(this.getComplexes, 300);
  },
  watch: {
    options: {
      deep: true,
      handler() {
        this.fetchData();
      }
    }
  },
  methods: {
    async clearComplex() {
      await this.getDevices(0);
      this.filter.complexId = null;
      this.selectedSubComplexId = null;
      this.subComplexes = [];
    },
    async resendSelected() {
      if (!this.selected.length) return;

      this.filter.deviceIds = this.selected.map(item => item.id);

      this.resendLoading = true;
      try {
        await request.post(
          "/notification/resend-selected",
          this.cleanObject(this.filter)
        );

        this.$toast.success("درخواست ارسال مجدد ثبت شد");
        this.selected = [];
        await this.fetchData();
      } catch {
        this.$toast.error("خطا در ارسال مجدد");
      } finally {
        this.resendLoading = false;
      }
    },
    onPageChange(newPage) {
      this.filter.page = newPage;
      this.fetchData();
    },
    onItemsPerPageChange(newItemsPerPage) {
      this.filter.itemsPerPage = newItemsPerPage;
      this.filter.page = 1;
      this.fetchData();
    },
    async getComplexes() {
      await request.get(`/publicData/main-complexes`).then(response => {
        var data = response.data.result;
        this.complexes = data;
      });
    },
    async getSubComplexes() {
      this.filter.deviceId = null;
      if (this.filter.complexId) {
        await request
          .get(`/publicData/sub-complexes/${this.filter.complexId}`)
          .then(response => {
            var data = response.data.result;
            this.subComplexes = data;
            this.getDevices(this.filter.complexId);
          });
      }
    },
    async getDevices(complexId) {
      if (complexId != null) {
        await request
          .get(`/publicData/main-complexes-devices/${complexId}`)
          .then(response => {
            var data = response.data.result;
            this.devices = data;
          });
      }
    },
    async fetchData() {
      this.loading = true;
      this.prepareDateFilters();
      try {
        const params = this.buildRequestParams();
        const res = await request.get("/notification/device-sms", { params });
        this.setResponseData(res);
      } catch (error) {
        console.error("Error fetching SMS data:", error);
      } finally {
        this.loading = false;
      }
    },
    prepareDateFilters() {
      this.filter.fromDate = this.from
        ? moment(this.from, "jYYYY/jMM/jDD").format("YYYY-MM-DD")
        : null;
      this.filter.toDate = this.to
        ? moment(this.to, "jYYYY/jMM/jDD").format("YYYY-MM-DD")
        : null;
    },
    buildRequestParams() {
      return {
        page: this.options.page,
        itemsPerPage: this.options.itemsPerPage,
        mustSort: true,
        sortBy: "createdAt",
        sortDesc: true,
        ...this.cleanObject(this.filter)
      };
    },
    setResponseData(res) {
      this.items = res.data.result.result || [];
      this.total = res.data.result.totalItems || 0;
    },
    applyFilter() {
      this.options.page = 1;
      this.fetchData();
    },
    cleanObject(obj) {
      return Object.fromEntries(
        Object.entries(obj).filter(
          ([, value]) => value !== null && value !== "" && value !== undefined
        )
      );
    },
    resendAllMessages() {
      this.showResendDialog = true;
    },
    async confirmResend() {
      this.resendAllLoading = true;
      this.showResendDialog = false;
      try {
        await request.post(
          "/notification/resend-all",
          this.cleanObject(this.filter)
        );
        await this.fetchData();
      } catch (error) {
        console.error("Error resending messages:", error);
      } finally {
        this.resendAllLoading = false;
      }
    },
    formatDate(date) {
      if (!date) return "-";
      return moment(date).format("jYYYY/jMM/jDD HH:mm");
    },
    getType(type) {
      return this.smsTypeMap[type] ?? "نامشخص";
    }
  }
};
</script>

<style scoped>
.IranSans {
  font-family: "IranSans", sans-serif !important;
}

.bread-row {
  padding: 12px 16px 0;
}

.sms-card {
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  background: #fff;
  overflow: hidden;
}

.title-font {
  font-family: "IranSans", sans-serif !important;
  font-weight: 600 !important;
}

.sms-table :deep(thead th) {
  font-size: 0.82rem !important;
  color: #555 !important;
  background-color: #f7f7f7 !important;
  white-space: nowrap;
}

.sms-table :deep(td) {
  font-size: 0.86rem !important;
  padding: 10px 12px !important;
}

.v-text-field--outlined :deep(.v-input__slot) {
  border-radius: 6px;
}

:deep(.vpd-is-editable) {
  border-top-left-radius: 6px !important;
  border-bottom-left-radius: 6px !important;
  height: 40px;
}
:deep(.vpd-icon-btn) {
  border-top-right-radius: 6px !important;
  border-bottom-right-radius: 6px !important;
}
/* ==================== RESPONSIVE FIXES ==================== */
@media (max-width: 600px) {
  .sms-card {
    border-radius: 0;
    border-left: none;
    border-right: none;
  }

  .bread-row {
    padding: 12px 16px 8px;
  }

  .v-card-title {
    padding: 12px 16px !important;
  }

  .v-card-text {
    padding: 0 16px 20px !important;
  }

  .sms-table :deep(table) {
    min-width: 920px; /* horizontal scroll on very small screens */
  }

  .v-btn {
    font-size: 0.8rem !important;
    padding: 0 10px !important;
  }
}

@media (max-width: 960px) {
  .title-font {
    font-size: 1.05rem !important;
  }
}
</style>
